const DEFAULT_EXTENSION = 'jpg';

export type PersistChatMediaFileParams = {
  id: string;
  uri: string;
  mimeType?: string | null;
  fileName?: string | null;
};

export type PersistedChatMediaFile = {
  uploadUri: string;
  previewUri: string;
  storageUri: string | null;
  mimeType: string | null;
};

function guessExtension({
  mimeType,
  fileName,
  uri,
}: {
  mimeType?: string | null;
  fileName?: string | null;
  uri?: string | null;
}) {
  if (mimeType) {
    const [, extension] = mimeType.split('/');

    if (extension) {
      return extension.toLowerCase();
    }
  }

  const nameSource = fileName || uri;

  if (nameSource) {
    const match = /\.([a-zA-Z0-9]+)$/.exec(nameSource);

    if (match?.[1]) {
      return match[1].toLowerCase();
    }
  }

  return DEFAULT_EXTENSION;
}

function getMimeTypeFromExtension(extension: string | null | undefined) {
  if (!extension) {
    return null;
  }

  switch (extension.toLowerCase()) {
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'png':
      return 'image/png';
    case 'gif':
      return 'image/gif';
    case 'heic':
    case 'heif':
      return 'image/heic';
    case 'webp':
      return 'image/webp';
    default:
      return null;
  }
}

function getDataUrl(base64: string, mimeType: string | null) {
  const resolvedMimeType = mimeType || 'application/octet-stream';

  return `data:${resolvedMimeType};base64,${base64}`;
}

async function blobToBase64(blob: Blob) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();

    reader.onerror = () => reject(reader.error);
    reader.onloadend = () => {
      const result = reader.result?.toString() || '';
      const [, base64] = result.split(',');
      resolve(base64 || '');
    };
    reader.readAsDataURL(blob);
  });
}

export async function persistChatMediaFile({
  mimeType,
  fileName,
  uri,
}: PersistChatMediaFileParams): Promise<PersistedChatMediaFile> {
  const extension = guessExtension({ mimeType, fileName, uri });
  const inferredMimeType =
    mimeType || getMimeTypeFromExtension(extension) || null;
  const response = await fetch(uri);
  const blob = await response.blob();
  const base64 = await blobToBase64(blob);
  const resolvedMimeType =
    inferredMimeType || blob.type || getMimeTypeFromExtension(extension);

  if (!base64) {
    throw new Error('Failed to persist chat media blob.');
  }

  const dataUrl = getDataUrl(base64, resolvedMimeType);

  return {
    uploadUri: dataUrl,
    previewUri: dataUrl,
    storageUri: null,
    mimeType: resolvedMimeType || null,
  };
}

export async function removePersistedChatMediaFile(
  storageUri: string | null | undefined,
) {
  if (storageUri) {
    return;
  }
  return;
}
